import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Wrench,
  Search,
  Plus,
  Star,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Clock,
  Users,
  FileText,
  ArrowLeft,
  Loader2,
  Building2,
  Briefcase,
  Shield,
  ThumbsUp,
  AlertTriangle,
  Send,
  DollarSign,
  Calendar,
  ChevronRight,
  Zap,
  Droplet,
  Paintbrush,
  Home,
  Bug,
  Wind,
  Sparkles,
  Flower,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  plumber: Droplet,
  electrician: Zap,
  gate_motor: Home,
  garden: Flower,
  security: Shield,
  painting: Paintbrush,
  general_maintenance: Wrench,
  cleaning: Sparkles,
  hvac: Wind,
  roofing: Home,
  pest_control: Bug,
  other: Wrench,
};

const formatCurrency = (amount) => `R${amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 }) || '0.00'}`;

export default function AdminContractors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('directory');
  const [contractors, setContractors] = useState([]);
  const [pendingContractors, setPendingContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Dialog states
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showContractorDialog, setShowContractorDialog] = useState(false);
  const [showQuotesDialog, setShowQuotesDialog] = useState(false);
  const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Job form
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    category: '',
    unit_number: '',
    location_details: '',
  });

  // Add Contractor form
  const [contractorForm, setContractorForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    categories: [],
    services_description: '',
    service_areas: '',
  });

  const hoaId = user?.hoa_id || 'demo_hoa';
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchData();
  }, [hoaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch categories
      const catRes = await fetch(`${API_URL}/api/contractors/categories`, { headers });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }

      // Fetch approved contractors
      const contRes = await fetch(`${API_URL}/api/contractors/list?status=approved`, { headers });
      if (contRes.ok) {
        setContractors(await contRes.json());
      }

      // Fetch pending contractors
      const pendRes = await fetch(`${API_URL}/api/contractors/list?status=pending`, { headers });
      if (pendRes.ok) {
        setPendingContractors(await pendRes.json());
      }

      // Fetch jobs
      const jobsRes = await fetch(`${API_URL}/api/contractors/jobs/${hoaId}`, { headers });
      if (jobsRes.ok) {
        setJobs(await jobsRes.json());
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/contractors/stats/${hoaId}`, { headers });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setCategories([
        { id: 'plumber', name: 'Plumber' },
        { id: 'electrician', name: 'Electrician' },
        { id: 'garden', name: 'Garden Services' },
        { id: 'general_maintenance', name: 'General Maintenance' },
      ]);
      setContractors([
        { contractor_id: 'c1', company_name: 'QuickFix Plumbing', contact_name: 'John Plumber', phone: '+27 82 555 1234', email: 'john@quickfix.co.za', categories: ['plumber'], average_rating: 4.5, total_ratings: 12, completed_jobs: 8, status: 'approved' },
        { contractor_id: 'c2', company_name: 'Spark Electric', contact_name: 'Mike Sparks', phone: '+27 83 555 5678', email: 'mike@sparkelectric.co.za', categories: ['electrician'], average_rating: 4.8, total_ratings: 25, completed_jobs: 20, status: 'approved' },
      ]);
      setJobs([
        { job_id: 'j1', title: 'Fix leaking pipe', category: 'plumber', status: 'open', quote_count: 2, created_at: new Date().toISOString() },
      ]);
      setStats({
        jobs: { open: 2, quoting: 1, in_progress: 1, completed: 5, total: 9 },
        contractors: { total: 8, pending_approval: 2 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveContractor = async (contractorId) => {
    try {
      const res = await fetch(`${API_URL}/api/contractors/${contractorId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Contractor approved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to approve contractor');
    }
  };

  const handleAddContractor = async () => {
    if (!contractorForm.company_name || !contractorForm.email || contractorForm.categories.length === 0) {
      toast.error('Company name, email, and at least one category are required');
      return;
    }

    setSubmitting(true);
    try {
      // Register the contractor
      const res = await fetch(`${API_URL}/api/contractors/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...contractorForm,
          service_areas: contractorForm.service_areas.split(',').map(s => s.trim()).filter(Boolean)
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Auto-approve the contractor
        await fetch(`${API_URL}/api/contractors/${data.contractor_id}/approve`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast.success('Contractor added and approved!', {
          description: `Portal link: /contractor-portal?id=${data.contractor_id}`
        });
        setShowAddContractorDialog(false);
        setContractorForm({
          company_name: '',
          contact_name: '',
          email: '',
          phone: '',
          categories: [],
          services_description: '',
          service_areas: '',
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add contractor');
      }
    } catch (error) {
      toast.error('Failed to add contractor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateJob = async () => {
    if (!jobForm.title || !jobForm.category) {
      toast.error('Title and category are required');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contractors/jobs/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          ticket_id: `ticket_${Date.now()}`, // Mock ticket ID
          ...jobForm
        })
      });

      if (res.ok) {
        toast.success('Job posted - contractors can now submit quotes');
        setShowJobDialog(false);
        setJobForm({ title: '', description: '', category: '', unit_number: '', location_details: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignContractor = async (jobId, contractorId, quoteId) => {
    try {
      const res = await fetch(`${API_URL}/api/contractors/jobs/${jobId}/assign?contractor_id=${contractorId}&quote_id=${quoteId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Contractor assigned to job');
        setShowQuotesDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign contractor');
    }
  };

  const filteredContractors = contractors.filter(c => {
    const matchesSearch = c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || c.categories?.includes(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700',
      quoting: 'bg-amber-100 text-amber-700',
      approved: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-sky-100 text-sky-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-slate-100 text-slate-500',
    };
    return colors[status] || colors.open;
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || Tool;
    return <Icon className="w-5 h-5" />;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`w-4 h-4 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
          />
        ))}
        <span className="ml-1 text-sm text-slate-600">{rating?.toFixed(1) || '0.0'}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="admin-contractors">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2 text-slate-600" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Contractor Marketplace</h1>
              <p className="text-slate-500 mt-1">Manage service providers and job requests</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddContractorDialog(true)} data-testid="add-contractor-btn">
                <Users className="w-4 h-4 mr-2" />
                Add Contractor
              </Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowJobDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Post New Job
              </Button>
            </div>
          </div>
        </div>
        {/* Stats */}
        {!loading && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.jobs?.open || 0}</p>
                    <p className="text-sm text-slate-500">Open Jobs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.jobs?.quoting || 0}</p>
                    <p className="text-sm text-slate-500">Awaiting Quotes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.contractors?.total || 0}</p>
                    <p className="text-sm text-slate-500">Contractors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.jobs?.completed || 0}</p>
                    <p className="text-sm text-slate-500">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="directory">Contractor Directory</TabsTrigger>
            <TabsTrigger value="jobs">
              Jobs
              {jobs.filter(j => j.status === 'open' || j.status === 'quoting').length > 0 && (
                <Badge className="ml-2 bg-amber-500">{jobs.filter(j => j.status === 'open' || j.status === 'quoting').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending">
              Pending Approval
              {pendingContractors.length > 0 && (
                <Badge className="ml-2 bg-red-500">{pendingContractors.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Directory Tab */}
          <TabsContent value="directory">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search contractors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Contractors Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredContractors.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No contractors found</h3>
                  <p className="text-slate-500">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredContractors.map(contractor => (
                  <Card 
                    key={contractor.contractor_id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedContractor(contractor);
                      setShowContractorDialog(true);
                    }}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <CategoryIcon category={contractor.categories?.[0]} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{contractor.company_name}</h3>
                            <p className="text-sm text-slate-500">{contractor.contact_name}</p>
                          </div>
                        </div>
                        {contractor.verified && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex flex-wrap gap-1">
                          {contractor.categories?.slice(0, 3).map(cat => (
                            <Badge key={cat} variant="secondary" className="text-xs">
                              {categories.find(c => c.id === cat)?.name || cat}
                            </Badge>
                          ))}
                        </div>
                        {renderStars(contractor.average_rating)}
                        <p className="text-xs text-slate-500">
                          {contractor.total_ratings} reviews • {contractor.completed_jobs} jobs completed
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Phone className="w-3 h-3" />
                          {contractor.phone}
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle>Job Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">No jobs yet</h3>
                    <p className="text-slate-500 mb-4">Post a job to request quotes from contractors</p>
                    <Button onClick={() => setShowJobDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Post New Job
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.map(job => (
                      <div 
                        key={job.job_id} 
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowQuotesDialog(true);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <CategoryIcon category={job.category} />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{job.title}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={getStatusColor(job.status)}>
                                {job.status?.replace('_', ' ')}
                              </Badge>
                              {job.quote_count > 0 && (
                                <span className="text-sm text-slate-500">
                                  {job.quote_count} quote{job.quote_count > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">
                            {new Date(job.created_at).toLocaleDateString()}
                          </p>
                          <ChevronRight className="w-5 h-5 text-slate-400 ml-auto mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Pending Contractor Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingContractors.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">All caught up!</h3>
                    <p className="text-slate-500">No pending contractor applications</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingContractors.map(contractor => (
                      <div key={contractor.contractor_id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{contractor.company_name}</h4>
                            <p className="text-sm text-slate-500">{contractor.contact_name} • {contractor.email}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {contractor.categories?.map(cat => (
                                <Badge key={cat} variant="secondary" className="text-xs">
                                  {categories.find(c => c.id === cat)?.name || cat}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApproveContractor(contractor.contractor_id)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Job Dialog */}
        <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Post New Job</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Job Title *</Label>
                <Input
                  value={jobForm.title}
                  onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                  placeholder="e.g., Fix leaking bathroom pipe"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={jobForm.category} onValueChange={(v) => {
                  if (v === '__add_new__') { setShowAddCategoryDialog(true); return; }
                  setJobForm({...jobForm, category: v});
                }}>
                  <SelectTrigger className="mt-1" data-testid="job-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-indigo-600 font-medium">+ Create new category…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={jobForm.description}
                  onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                  placeholder="Describe the work needed..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unit Number</Label>
                  <Input
                    value={jobForm.unit_number}
                    onChange={(e) => setJobForm({...jobForm, unit_number: e.target.value})}
                    placeholder="e.g., A-101"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Location Details</Label>
                  <Input
                    value={jobForm.location_details}
                    onChange={(e) => setJobForm({...jobForm, location_details: e.target.value})}
                    placeholder="e.g., Main bathroom"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowJobDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateJob} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Job'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contractor Detail Dialog */}
        <Dialog open={showContractorDialog} onOpenChange={setShowContractorDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedContractor?.company_name}</DialogTitle>
            </DialogHeader>
            {selectedContractor && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <CategoryIcon category={selectedContractor.categories?.[0]} />
                  </div>
                  <div>
                    <p className="font-medium">{selectedContractor.contact_name}</p>
                    {renderStars(selectedContractor.average_rating)}
                    <p className="text-sm text-slate-500">
                      {selectedContractor.total_ratings} reviews • {selectedContractor.completed_jobs} jobs
                    </p>
                  </div>
                </div>

                <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedContractor.phone}`} className="text-indigo-600">{selectedContractor.phone}</a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedContractor.email}`} className="text-indigo-600">{selectedContractor.email}</a>
                  </div>
                  {selectedContractor.address && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedContractor.address}</span>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-500">Services</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedContractor.categories?.map(cat => (
                      <Badge key={cat} variant="secondary">
                        {categories.find(c => c.id === cat)?.name || cat}
                      </Badge>
                    ))}
                  </div>
                </div>

                {selectedContractor.services_description && (
                  <div>
                    <Label className="text-slate-500">Description</Label>
                    <p className="mt-1 text-sm">{selectedContractor.services_description}</p>
                  </div>
                )}

                {selectedContractor.service_areas?.length > 0 && (
                  <div>
                    <Label className="text-slate-500">Service Areas</Label>
                    <p className="mt-1 text-sm">{selectedContractor.service_areas.join(', ')}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContractorDialog(false)}>Close</Button>
              <Button asChild>
                <a href={`tel:${selectedContractor?.phone}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quotes Dialog */}
        <Dialog open={showQuotesDialog} onOpenChange={setShowQuotesDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Job: {selectedJob?.title}</DialogTitle>
            </DialogHeader>
            {selectedJob && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getStatusColor(selectedJob.status)}>
                      {selectedJob.status?.replace('_', ' ')}
                    </Badge>
                    <Badge variant="outline">
                      {categories.find(c => c.id === selectedJob.category)?.name || selectedJob.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">{selectedJob.description || 'No description'}</p>
                </div>

                <div>
                  <Label className="text-slate-700 font-semibold mb-3 block">
                    Quotes Received ({selectedJob.quote_count || 0})
                  </Label>
                  
                  {selectedJob.quotes?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedJob.quotes.map(quote => (
                        <div key={quote.quote_id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-medium">{quote.contractor_name}</p>
                            <p className="text-sm text-slate-500">{quote.description}</p>
                            <p className="text-sm text-slate-400">
                              {quote.estimated_days} day{quote.estimated_days > 1 ? 's' : ''} • Valid until {new Date(quote.valid_until).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-emerald-600">{formatCurrency(quote.amount)}</p>
                            {quote.status === 'pending' && (
                              <Button 
                                size="sm" 
                                className="mt-2"
                                onClick={() => handleAssignContractor(selectedJob.job_id, quote.contractor_id, quote.quote_id)}
                              >
                                Accept Quote
                              </Button>
                            )}
                            {quote.status === 'accepted' && (
                              <Badge className="bg-emerald-100 text-emerald-700">Accepted</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No quotes received yet</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuotesDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Contractor Dialog */}
        <Dialog open={showAddContractorDialog} onOpenChange={setShowAddContractorDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Contractor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={contractorForm.company_name}
                  onChange={(e) => setContractorForm({...contractorForm, company_name: e.target.value})}
                  placeholder="e.g., QuickFix Plumbing"
                  className="mt-1"
                  data-testid="contractor-company-input"
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={contractorForm.contact_name}
                  onChange={(e) => setContractorForm({...contractorForm, contact_name: e.target.value})}
                  placeholder="Full name"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={contractorForm.email}
                    onChange={(e) => setContractorForm({...contractorForm, email: e.target.value})}
                    placeholder="email@company.com"
                    className="mt-1"
                    data-testid="contractor-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={contractorForm.phone}
                    onChange={(e) => setContractorForm({...contractorForm, phone: e.target.value})}
                    placeholder="+27 82 555 1234"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Service Categories *</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {categories.map(cat => (
                    <label 
                      key={cat.id} 
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        contractorForm.categories.includes(cat.id) 
                          ? 'bg-indigo-50 border-indigo-300' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={contractorForm.categories.includes(cat.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setContractorForm({...contractorForm, categories: [...contractorForm.categories, cat.id]});
                          } else {
                            setContractorForm({...contractorForm, categories: contractorForm.categories.filter(c => c !== cat.id)});
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Service Areas</Label>
                <Input
                  value={contractorForm.service_areas}
                  onChange={(e) => setContractorForm({...contractorForm, service_areas: e.target.value})}
                  placeholder="e.g., Sandton, Randburg, Fourways"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated list of areas served</p>
              </div>
              <div>
                <Label>Services Description</Label>
                <Textarea
                  value={contractorForm.services_description}
                  onChange={(e) => setContractorForm({...contractorForm, services_description: e.target.value})}
                  placeholder="Brief description of services offered..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddContractorDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleAddContractor} 
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-contractor-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add & Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Custom Category Dialog */}
        <Dialog open={showAddCategoryDialog} onOpenChange={(o) => { if (!o) { setShowAddCategoryDialog(false); setNewCategoryName(''); } }}>
          <DialogContent data-testid="add-category-dialog">
            <DialogHeader>
              <DialogTitle>Create Custom Contractor Category</DialogTitle>
              <DialogDescription>Add a new service category for your community (e.g., {'"Pool Maintenance", "Locksmith"'}).</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Category Name *</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g., Pool Maintenance"
                maxLength={50}
                data-testid="new-category-name-input"
              />
              <p className="text-xs text-slate-500">Up to 50 characters. Will be available in all category pickers.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddCategoryDialog(false); setNewCategoryName(''); }}>Cancel</Button>
              <Button
                disabled={addingCategory || !newCategoryName.trim()}
                onClick={async () => {
                  setAddingCategory(true);
                  try {
                    const res = await fetch(`${API_URL}/api/contractors/categories/custom`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ name: newCategoryName.trim() }),
                    });
                    const data = await res.json();
                    if (res.ok) {
                      toast.success(`Added "${data.category.name}"`);
                      setCategories((cs) => [...cs, data.category]);
                      setJobForm((j) => ({ ...j, category: data.category.id }));
                      setShowAddCategoryDialog(false);
                      setNewCategoryName('');
                    } else {
                      toast.error(data.detail || 'Could not add category');
                    }
                  } catch { toast.error('Network error'); }
                  finally { setAddingCategory(false); }
                }}
                data-testid="create-category-btn"
              >
                {addingCategory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
